import { Clock, Euro, Link, File, Train, Bus, Car } from "lucide-react";
import { TransportationActivity, ItineraryActivity } from "../models/activity.model";
import { BaseActivityCard } from "./BaseActivityCard";
import { DocumentationList } from "@/components/ui/documentation-list";

interface TransportationCardProps {
  activity: TransportationActivity;
  onEdit: (activity: ItineraryActivity) => void;
  onDelete: (activity: ItineraryActivity) => void;
  onDuplicate?: (activity: ItineraryActivity) => void;
  onExpand?: () => void;
  isReadOnly?: boolean;
  onDragStart?: (activity: ItineraryActivity) => void;
  onDropBefore?: (sourceActivityId: string, targetActivityId?: string) => void;
  onDragEnd?: () => void;
  isTraveler?: boolean;
  isAdmin?: boolean;
}

export const TransportationCard = ({
  activity,
  onEdit,
  onDelete,
  onDuplicate,
  onExpand,
  isReadOnly = false,
  onDragStart,
  onDropBefore,
  onDragEnd,
  isTraveler = true,
  isAdmin = false
}: TransportationCardProps) => {
  // Helper function to get transport icon
  const getTransportIcon = (transport_type: string) => {
    switch (transport_type) {
      case 'train': return <Train className="h-4 w-4" />;
      case 'bus': return <Bus className="h-4 w-4" />;
      case 'car': return <Car className="h-4 w-4" />;
      case 'taxi': return <Car className="h-4 w-4" />;
      case 'metro': return <Train className="h-4 w-4" />;
      default: return <Car className="h-4 w-4" />;
    }
  };

  const renderAlwaysVisible = () => (
    <>
      {activity.travels && activity.travels.length > 0 && (
        <div className="flex items-center gap-2">
          {getTransportIcon(activity.transport_type)}
          <span>{activity.travels[0].address} → {activity.travels[activity.travels.length - 1].address}</span>
        </div>
      )}
      {activity.travels && activity.travels.length > 0 && activity.travels[0].time && (
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span>{activity.travels[0].time}</span>
        </div>
      )}
      {activity.travels && activity.travels.length > 2 && (
        <div className="flex items-center gap-2">
          <span>{activity.travels.length - 2 === 1 ? `1 stop` : `${activity.travels.length - 2} stops`}</span>
        </div>
      )}
      {activity.cost && (isAdmin || !isTraveler) && (
        <div className="flex items-center gap-2">
          <Euro className="h-4 w-4" />
          <span>{activity.cost}</span>
        </div>
      )}
    </>
  );

  const renderCollapsible = () => (
    <>
      {/* Description */}
      {/* Note sempre visibile */}
      {activity.description && (
        <div className="mb-4">
          <p className="text-sm text-gray-600 leading-relaxed">{activity.description}</p>
        </div>
      )}

      {/* Company */}
      {activity.company && (
        <div className="mb-4">
          <span className="text-sm font-medium mb-1 block">Company</span>
          <p className="text-sm text-gray-600">{activity.company}</p>
        </div>
      )}

      {/* Travel Route */}
      {activity.travels && activity.travels.length > 0 && (
        <div className="mb-4">
          <span className="text-sm font-medium mb-1 block">Travel Route</span>
          <div className="space-y-2">
            {activity.travels.map((travel, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">
                  <div className="font-medium">
                    {index === 0 ? 'From ' : index === activity.travels.length - 1 ? 'To ' : `Stop ${index} `}
                    <span className="font-normal">{travel.address}</span>
                  </div>
                  <div>{travel.date} at {travel.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documentation */}
      <DocumentationList documentation={activity.documentation || []} />

      {/* Link */}
      {activity.link && (isAdmin || !isTraveler) && (
        <a
          href={activity.link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 mt-2 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <Link className="h-3 w-3 flex-shrink-0" />
          <span className="truncate max-w-[200px]">{activity.link}</span>
        </a>
      )}
    </>
  );

  return (
    <BaseActivityCard
      activity={activity}
      onEdit={onEdit}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
      onDragStart={onDragStart}
      onDropBefore={onDropBefore}
      onDragEnd={onDragEnd}
      onExpand={onExpand}
      isReadOnly={isReadOnly}
      isTraveler={isTraveler}
      isAdmin={isAdmin}
    >
      {{
        alwaysVisible: renderAlwaysVisible(),
        collapsible: renderCollapsible()
      }}
    </BaseActivityCard>
  );
};
